import { create } from 'zustand';

import RelationController, { ILikesResponse } from '@/api/relation/relation.ts';
import { Contact } from '@/api/messaging/types.ts';
import { Contact as RelationContact } from '@/api/relation/types.ts';
import {
  AlcoholPreference,
  BodyType,
  EmploymentSector,
  RelationKind,
  SmokingStatus,
  ZodiacSign
} from '@/enums/apiEnums.ts';

import { catchErrors } from '@/utils/sentry-monitor.tsx';
import { PhotoKind } from '@/shared/type/userDataFields.ts';

interface LikesListStore {
  likes: RelationContact[];
  pageToken: string | undefined;
  allLikesLoaded: boolean;
  isFetching: boolean;
  loadLikes: (pageToken?: string) => Promise<void>;
  like: (hid: string) => void;
  appendLikeFromWS: (contact: Contact) => void;
  reset: () => void;
}

export const LIKES_LIMIT = 40;

export const useLikesListStore = create<LikesListStore>((set, get) => ({
  likes: [],
  pageToken: undefined,
  allLikesLoaded: false,
  isFetching: false,
  loadLikes: async (pageToken) => {
    try {
      const { isFetching, allLikesLoaded } = get();

      if (isFetching || allLikesLoaded) return;

      set({ isFetching: true });

      await new Promise((resolve) => setTimeout(resolve, 250));

      let newLikes: ILikesResponse;

      if (pageToken) {
        newLikes = await RelationController.getLikesMe(LIKES_LIMIT, pageToken);
      } else {
        newLikes = await RelationController.getLikesMe(LIKES_LIMIT);
      }

      const pToken =
        newLikes.next_page_token.length !== 0
          ? newLikes.next_page_token
          : undefined;

      set((prev) => {
        const mergedLikes = pageToken
          ? [...prev.likes, ...newLikes.contacts]
          : [...newLikes.contacts, ...prev.likes];

        const uniqueLikesMap = new Map<string, RelationContact>();

        for (const like of mergedLikes) {
          if (!uniqueLikesMap.has(like.profile.hid)) {
            uniqueLikesMap.set(like.profile.hid, like);
          }
        }

        return {
          allLikesLoaded: newLikes.contacts.length < LIKES_LIMIT,
          likes: Array.from(uniqueLikesMap.values()),
          pageToken: pToken,
          isFetching: false
        };
      });
    } catch (e) {
      catchErrors(e, true);
    }
  },
  like: (hid) => {
    const currentLikes = get().likes;
    set({ likes: currentLikes.filter((like) => like.profile.hid !== hid) });
  },
  appendLikeFromWS: (contact: Contact) => {
    const newLike: RelationContact = {
      relation_kind: RelationKind.KIND_LIKES_YOU,
      distance: 0,
      profile: {
        hid: contact.hid,
        name: contact.name,
        age: contact.age,
        gender: contact.gender,
        online_state: contact.online_state,
        v2photos: contact.photo_url
          ? [
              {
                variants: [
                  {
                    url: contact.photo_url,
                    kind: PhotoKind.KIND_PRIMARY,
                    thumbhash: ''
                  }
                ]
              }
            ]
          : [],
        city: { id: 1, name: 'Kyiv', country: { id: 1, name: 'Ukraine' } },
        about_me: undefined,
        employment_sector: EmploymentSector.EMPLOYMENT_SECTOR_UNSPECIFIED,
        height_cm: 0,
        state: undefined,
        body_type: BodyType.BODY_TYPE_UNSPECIFIED,
        smoking_status: SmokingStatus.SMOKING_STATUS_UNSPECIFIED,
        alcohol_preference: AlcoholPreference.ALCOHOL_PREFERENCE_UNSPECIFIED,
        zodiac_sign: ZodiacSign.ZODIAC_SIGN_UNSPECIFIED,
        spoken_languages: []
      }
    };

    set((prev) => ({
      likes: [newLike, ...prev.likes]
    }));
  },
  reset: () => {
    set({
      likes: [],
      pageToken: undefined,
      allLikesLoaded: false,
      isFetching: false
    });
  }
}));
